export const DocumentConversionFormat = {
  Xlsx: 'xlsx',
  Docx: 'docx',
  Pptx: 'pptx',
  Csv: 'csv',
  Numbers: 'numbers',
  Pages: 'pages',
  Passthrough: 'passthrough',
  Unsupported: 'unsupported',
} as const;
export type DocumentConversionFormat =
  (typeof DocumentConversionFormat)[keyof typeof DocumentConversionFormat];

export const DOCUMENT_CONVERSION_INLINE_TOKEN_LIMIT = 6000;
export const DOCUMENT_CONVERSION_DEFAULT_PAGE_LIMIT = 200;
export const DOCUMENT_CONVERSION_MAX_INLINE_ROWS = 200;

export const LLM_READABLE_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/tab-separated-values',
  'text/html',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.apple.numbers',
  'application/x-iwork-numbers-sffnumbers',
  'application/vnd.apple.pages',
  'application/x-iwork-pages-sffpages',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

export const LLM_CHAT_ATTACHMENT_ACCEPTED_MIME_TYPES = [
  ...LLM_READABLE_DOCUMENT_MIME_TYPES,
] as const;

export const LLM_CHAT_LEGACY_OFFICE_MIME_TYPES = [
  'application/msword',
  'application/vnd.ms-excel',
] as const;

export function normalizeAttachmentFilename(filename: string): string {
  return filename.trim().toLowerCase();
}

export function isLegacyLlmChatOfficeAttachment(input: {
  filename: string;
  mimeType?: string;
}): boolean {
  const normalizedMime = input.mimeType?.split(';')[0]?.trim().toLowerCase();
  if (
    normalizedMime &&
    (LLM_CHAT_LEGACY_OFFICE_MIME_TYPES as readonly string[]).includes(normalizedMime)
  ) {
    return true;
  }

  const lower = normalizeAttachmentFilename(input.filename);
  const isLegacyDoc = lower.endsWith('.doc') && !lower.endsWith('.docx');
  const isLegacyXls =
    lower.endsWith('.xls') && !lower.endsWith('.xlsx') && !lower.endsWith('.xlsm');

  return isLegacyDoc || isLegacyXls;
}

export type LegacyOfficeTargetFormat = 'docx' | 'xlsx';

export function resolveLegacyOfficeTargetFormat(input: {
  filename: string;
  mimeType?: string;
}): LegacyOfficeTargetFormat | null {
  if (!isLegacyLlmChatOfficeAttachment(input)) {
    return null;
  }

  const normalizedMime = input.mimeType?.split(';')[0]?.trim().toLowerCase();
  if (normalizedMime === 'application/msword') {
    return 'docx';
  }
  if (normalizedMime === 'application/vnd.ms-excel') {
    return 'xlsx';
  }

  const lower = normalizeAttachmentFilename(input.filename);
  if (lower.endsWith('.doc') && !lower.endsWith('.docx')) {
    return 'docx';
  }
  if (lower.endsWith('.xls') && !lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
    return 'xlsx';
  }

  return null;
}

export function resolveModernOfficeMimeType(target: LegacyOfficeTargetFormat): string {
  if (target === 'docx') {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

export function resolveModernOfficeFilename(
  filename: string,
  target: LegacyOfficeTargetFormat,
): string {
  const extension = target === 'docx' ? '.docx' : '.xlsx';
  const lower = normalizeAttachmentFilename(filename);
  if (target === 'docx' && lower.endsWith('.doc') && !lower.endsWith('.docx')) {
    return filename.slice(0, -4) + extension;
  }
  if (target === 'xlsx' && lower.endsWith('.xls') && !lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
    return filename.slice(0, -4) + extension;
  }
  if (!lower.includes('.')) {
    return `${filename}${extension}`;
  }
  return filename.replace(/\.[^.]+$/, extension);
}

export interface ConversionSheetMeta {
  name: string;
  rows: number;
  cols: number;
}

export interface ConversionResultMeta {
  sheets?: ConversionSheetMeta[];
  truncated: boolean;
}

export interface ConversionResult {
  markdown: string;
  format: DocumentConversionFormat;
  meta: ConversionResultMeta;
  approxTokens: number;
}

export interface ReadAttachmentSliceResult {
  attachmentId: string;
  filename: string;
  mimeType: string;
  sheet?: string;
  citation: string;
  markdown: string;
  offset: number;
  limit: number;
  totalRows: number;
  hasMore: boolean;
}

export interface OversizedAttachmentHint {
  attachmentId: string;
  filename: string;
  mimeType: string;
  approxTokens: number;
  sheets?: ConversionSheetMeta[];
}
